export const DESKTOP_CACHE_HEADER = "x-billsarthi-desktop-cache";

export const isDesktopApp = () =>
  typeof window !== "undefined" &&
  window.desktopRuntime?.isElectron === true &&
  Boolean(window.desktopOrders && window.desktopOrderDetails);

export const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const indiaDateKey = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = /^\d{4}-\d{2}-\d{2}\s/.test(raw)
    ? `${raw.replace(" ", "T")}Z`
    : raw;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return raw.slice(0, 10);
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(parsed).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const extractOrderItems = (detail, order) => {
  const detailOrder = detail?.orderdetails || detail?.order || {};
  const candidates = [
    detail?.itemslist,
    detail?.itemlist,
    detail?.orderitems,
    detail?.items,
    detailOrder?.itemslist,
    detailOrder?.orderitems,
    detailOrder?.itemlist,
    detailOrder?.items,
    order?.itemslist,
    order?.orderitems,
    order?.itemlist,
    order?.items,
    order?.payload?.itemlist,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
    if (typeof c === "string") {
      try {
        const parsed = JSON.parse(c);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
    }
  }
  return [];
};

export const getCachedOrderDetail = async (restaurantId, order) => {
  if (!isDesktopApp()) return null;
  const keys = [...new Set([
    order?.id,
    order?.orderid,
    order?.order_id,
    order?.ordercode,
  ].filter((key) => key !== null && key !== undefined && String(key).trim() !== ""))];
  for (const orderId of keys) {
    const detail = await window.desktopOrderDetails.get({ restaurantId, orderId });
    if (detail) return detail;
  }
  return null;
};

const parseJSON = (val, fallback = []) => {
  try {
    const parsed = JSON.parse(val);
    return parsed !== null && parsed !== undefined ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export const getLocalProductAndCategoryMaps = async (restaurantId = null) => {
  const productMap = new Map();
  const productByNameMap = new Map();
  const categoryMap = new Map();

  const addProduct = (p) => {
    if (!p) return;
    const pId = p?.id ?? p?.product_id ?? p?.productid;
    if (pId !== undefined && pId !== null && String(pId).trim() !== "" && String(pId) !== "0") {
      if (!productMap.has(String(pId))) {
        productMap.set(String(pId), p);
      }
    }
    const pName = String(p?.name ?? p?.product_name ?? p?.item_name ?? p?.title ?? "").trim().toLowerCase();
    if (pName && !productByNameMap.has(pName)) {
      productByNameMap.set(pName, p);
    }
  };

  const addCategory = (c) => {
    if (!c) return;
    const cId = c?.id ?? c?.category_id ?? c?.categoryid;
    if (cId !== undefined && cId !== null && String(cId).trim() !== "") {
      if (!categoryMap.has(String(cId))) {
        categoryMap.set(String(cId), c);
      }
    }
  };

  if (restaurantId && typeof window !== "undefined" && window.desktopProducts?.get) {
    try {
      const desktopProds =
        (await window.desktopProducts.get({ restaurantId })) ||
        (await window.desktopProducts.get({ restaurantId, source: "master" })) ||
        [];
      if (Array.isArray(desktopProds)) {
        desktopProds.forEach(addProduct);
      }
    } catch (e) {
      console.warn("Unable to load desktopProducts cache", e);
    }
  }

  const prodSources = [
    parseJSON(localStorage.getItem("productlistx")),
    parseJSON(localStorage.getItem("allProducts")),
    parseJSON(localStorage.getItem("createorderitem")),
  ];
  prodSources.forEach((list) => {
    if (Array.isArray(list)) list.forEach(addProduct);
  });

  const catSources = [
    parseJSON(localStorage.getItem("categorylistx")),
    parseJSON(localStorage.getItem("productcategory")),
  ];
  catSources.forEach((list) => {
    if (Array.isArray(list)) list.forEach(addCategory);
  });

  return { productMap, productByNameMap, categoryMap };
};

export const getCategoryNameFromItem = (item, productMap = null, productByNameMap = null, categoryMap = null) => {
  if (!item) return "Uncategorized";

  const isInvalidCategory = (name) => {
    if (!name || typeof name !== "string") return true;
    const clean = name.trim().toLowerCase();
    return (
      !clean ||
      clean === "uncategorized" ||
      clean === "null" ||
      clean === "undefined" ||
      clean === "-"
    );
  };

  const resolveFromObj = (obj) => {
    if (!obj) return null;

    const candidates = [
      obj?.category_name,
      obj?.categoryName,
      obj?.productcategoryname,
      obj?.product_category_name,
      obj?.productcategory_name,
      obj?.cat_name,
      obj?.categoryname,
      obj?.category_title,
      typeof obj?.category === "string" ? obj.category : null,
      typeof obj?.category === "object"
        ? obj.category?.name || obj.category?.category_name || obj.category?.productcategoryname
        : null,
      typeof obj?.product_category === "string" ? obj.product_category : null,
      typeof obj?.product_category === "object"
        ? obj.product_category?.name ||
          obj.product_category?.category_name ||
          obj.product_category?.productcategoryname
        : null,
      typeof obj?.productcategory === "string" ? obj.productcategory : null,
      typeof obj?.productcategory === "object"
        ? obj.productcategory?.name || obj.productcategory?.category_name
        : null,
    ];

    for (const c of candidates) {
      if (!isInvalidCategory(c)) {
        return c.trim();
      }
    }

    const catId = obj?.productcategoryid ?? obj?.category_id ?? obj?.categoryid ?? obj?.categoryId;
    if (categoryMap && catId !== undefined && catId !== null && categoryMap.has(String(catId))) {
      const catObj = categoryMap.get(String(catId));
      const catName = catObj?.productcategoryname || catObj?.name || catObj?.category_name;
      if (!isInvalidCategory(catName)) {
        return catName.trim();
      }
    }

    return null;
  };

  let resolved = resolveFromObj(item);
  if (resolved) return resolved;

  const productId = item?.product_id ?? item?.productid ?? item?.id ?? item?.item_id;
  if (productMap && productId !== undefined && productId !== null && productMap.has(String(productId))) {
    const prod = productMap.get(String(productId));
    resolved = resolveFromObj(prod);
    if (resolved) return resolved;
  }

  const itemName = String(
    item?.item_name ?? item?.product_name ?? item?.productname ?? item?.name ?? ""
  )
    .trim()
    .toLowerCase();
  if (productByNameMap && itemName && productByNameMap.has(itemName)) {
    const prod = productByNameMap.get(itemName);
    resolved = resolveFromObj(prod);
    if (resolved) return resolved;
  }

  const baseName = itemName.split("(")[0].split("-")[0].split("+")[0].trim();
  if (baseName && baseName !== itemName && productByNameMap && productByNameMap.has(baseName)) {
    const prod = productByNameMap.get(baseName);
    resolved = resolveFromObj(prod);
    if (resolved) return resolved;
  }

  const fallbackCandidates = [
    item?.category_name,
    item?.categoryName,
    item?.productcategoryname,
    item?.product_category_name,
    item?.productcategory_name,
    item?.cat_name,
    item?.categoryname,
    item?.category_title,
    typeof item?.category === "string" ? item.category : null,
    typeof item?.product_category === "string" ? item.product_category : null,
    typeof item?.productcategory === "string" ? item.productcategory : null,
  ];

  for (const c of fallbackCandidates) {
    if (c && typeof c === "string" && c.trim() !== "") {
      return c.trim();
    }
  }

  return "Uncategorized";
};
